/**
 * B2BReturnPage — Phase 62: RMA Submission
 *
 * Route: /b2b/returns  (nested under B2BLayout)
 *
 * Allows B2B clients and agents to submit a Return Merchandise Authorization
 * (RMA) request without creating a "negative order".
 *
 * Flow:
 *   1. Client browses previously ordered products (or searches catalogue).
 *   2. Selects items + quantities to return + optional per-item reason.
 *   3. Adds a global return reason.
 *   4. Taps "שלח בקשת החזרה" → calls submit_rma() RPC.
 *   5. Back-office reviews and approves in RMAAdminTab (InventoryMovementsPage).
 *
 * The page intentionally mirrors the B2B order cart UI:
 *   - Same product search / list look-and-feel.
 *   - Distinct warm-orange / amber colour theme to signal "return" context.
 *   - Clear header banner: "בקשת החזרה" (not "הזמנה").
 *
 * Security: submit_rma() is SECURITY DEFINER and validates client ownership.
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, Navigate }  from 'react-router-dom';
import { supabase }                  from '@/lib/supabase';
import { useAuth }                   from '@/features/auth/AuthContext';
import { useB2BUser }                from './B2BUserContext';
import { useB2BTerms } from './B2BTermsContext';
import {
  Loader2,
  AlertCircle,
  RotateCcw,
  Plus,
  Minus,
  Trash2,
  Search,
  CheckCircle2,
  Package,
  ChevronRight,
  Send,
  X,
  ClipboardList,
  BookOpen,
  MessageSquare,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ReturnProduct {
  id:         string;
  company_id: string;
  sku:        string;
  name:       string;
  price:      number;
  image_url:  string | null;
}

interface ReturnLine {
  product:      ReturnProduct;
  qty:          number;
  return_reason: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtPrice(n: number) {
  return `₪${n.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── Product image ────────────────────────────────────────────────────────────

function ProductImage({ src, alt }: { src: string | null; alt: string }) {
  if (!src) {
    return (
      <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center shrink-0">
        <Package className="w-5 h-5 text-gray-300" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className="w-12 h-12 rounded-xl object-cover shrink-0"
      loading="lazy"
    />
  );
}

// ─── Qty stepper ──────────────────────────────────────────────────────────────

function QtyStepper({
  value,
  onChange,
}: { value: number; onChange: (n: number) => void }) {
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, value - 1))}
        className="w-7 h-7 flex items-center justify-center rounded-lg border
                   border-orange-200 text-orange-600 hover:bg-orange-50
                   active:scale-90 transition-all"
      >
        <Minus className="w-3 h-3" />
      </button>
      <input
        type="number"
        min={1}
        value={value}
        onChange={e => {
          const n = parseInt(e.target.value, 10);
          if (!isNaN(n) && n >= 1) onChange(n);
        }}
        className="w-10 h-7 text-center text-sm font-bold border border-orange-200
                   rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300
                   text-orange-700 bg-orange-50/50"
      />
      <button
        type="button"
        onClick={() => onChange(value + 1)}
        className="w-7 h-7 flex items-center justify-center rounded-lg border
                   border-orange-200 text-orange-600 hover:bg-orange-50
                   active:scale-90 transition-all"
      >
        <Plus className="w-3 h-3" />
      </button>
    </div>
  );
}

// ─── Product search row ───────────────────────────────────────────────────────

function ProductRow({
  product,
  onAdd,
}: { product: ReturnProduct; onAdd: (p: ReturnProduct) => void }) {
  return (
    <button
      onClick={() => onAdd(product)}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl
                 hover:bg-orange-50 active:bg-orange-100 transition-colors text-right"
    >
      <ProductImage src={product.image_url} alt={product.name} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">{product.name}</p>
        <p className="text-[11px] text-gray-400 font-mono">{product.sku}</p>
      </div>
      <div className="shrink-0 text-right">
        <p className="text-sm font-bold text-gray-800">{fmtPrice(product.price)}</p>
        <p className="text-[10px] text-orange-500 font-semibold">הוסף +</p>
      </div>
    </button>
  );
}

// ─── Return line row in the "basket" ─────────────────────────────────────────

function ReturnLineRow({
  line,
  onQtyChange,
  onReasonChange,
  onRemove,
}: {
  line:           ReturnLine;
  onQtyChange:    (productId: string, qty: number) => void;
  onReasonChange: (productId: string, reason: string) => void;
  onRemove:       (productId: string) => void;
}) {
  const [showReason, setShowReason] = useState(false);

  return (
    <div className="rounded-xl border border-orange-100 bg-orange-50/40 p-3 space-y-2">
      <div className="flex items-start gap-3">
        <ProductImage src={line.product.image_url} alt={line.product.name} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">{line.product.name}</p>
          <p className="text-[11px] text-gray-400 font-mono">{line.product.sku}</p>
          <p className="text-xs text-gray-600 mt-0.5">{fmtPrice(line.product.price)} / יח׳</p>
        </div>
        <button
          onClick={() => onRemove(line.product.id)}
          className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-red-500
                     hover:bg-red-50 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex items-center justify-between">
        <QtyStepper
          value={line.qty}
          onChange={qty => onQtyChange(line.product.id, qty)}
        />
        <button
          onClick={() => setShowReason(s => !s)}
          className={`flex items-center gap-1 text-[11px] font-medium rounded-lg
                      px-2 py-1 transition-colors
                      ${showReason || line.return_reason
                        ? 'text-orange-600 bg-orange-100'
                        : 'text-gray-400 hover:text-gray-600'
                      }`}
        >
          <MessageSquare className="w-3 h-3" />
          {showReason ? 'הסתר סיבה' : 'הוסף סיבה'}
        </button>
      </div>

      {(showReason || line.return_reason) && (
        <input
          type="text"
          value={line.return_reason}
          onChange={e => onReasonChange(line.product.id, e.target.value)}
          placeholder="סיבת ההחזרה לפריט זה (אופציונלי)…"
          className="w-full text-xs rounded-lg border border-orange-200 px-2.5 py-1.5
                     focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white
                     placeholder:text-gray-300"
        />
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function B2BReturnPage() {
  const { user }    = useAuth();
  const { session } = useB2BUser();
  const navigate    = useNavigate();
  const location    = useLocation();

  // Phase 112: redirect when returns disabled for this company
  const [returnsEnabled, setReturnsEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    const companyId = session?.activeCompanyId;
    if (!companyId) return;
    supabase
      .from('companies')
      .select('b2b_returns_enabled')
      .eq('id', companyId)
      .single()
      .then(({ data }) => {
        setReturnsEnabled(data ? Boolean((data as { b2b_returns_enabled: boolean }).b2b_returns_enabled) : true);
      });
  }, [session?.activeCompanyId]);

  const navState = location.state as
    | { allowedCompanyIds?: string[]; companyId?: string }
    | null | undefined;

  // ── State ────────────────────────────────────────────────────────────────

  const [products,     setProducts]     = useState<ReturnProduct[]>([]);
  const [loadingProds, setLoadingProds] = useState(false);
  const [search,       setSearch]       = useState('');
  const [lines,        setLines]        = useState<ReturnLine[]>([]);
  const [globalReason, setGlobalReason] = useState('');
  const [submitting,   setSubmitting]   = useState(false);
  const [error,        setError]        = useState<string | null>(null);
  const [success,      setSuccess]      = useState(false);
  const [rmaId,        setRmaId]        = useState<string | null>(null);

  // ── Resolve company/client context ───────────────────────────────────────

  const ctx = useMemo(() => {
    if (!session) return null;
    const record = session.activeClientRecord;
    if (!record) return null;
    return {
      company_id: record.company_id,
      client_id:  record.id,
      agent_id:   session.role === 'agent' ? (user?.id ?? null) : null,
    };
  }, [session, user]);

  // ── Fetch products for the active supplier ───────────────────────────────

  const fetchProducts = useCallback(async () => {
    if (!ctx) return;
    setLoadingProds(true);
    try {
      const { data, error: dbErr } = await supabase
        .from('inventory_products')
        .select('id, company_id, sku, name, price_retail_excl_vat, image_url')
        .eq('company_id', ctx.company_id)
        .eq('status', 'active')
        .order('name');

      if (dbErr) throw dbErr;

      setProducts(
        ((data ?? []) as any[]).map((p: any) => ({
          id:         p.id,
          company_id: p.company_id,
          sku:        p.sku,
          name:       p.name,
          price:      Number(p.price_retail_excl_vat ?? 0),
          image_url:  p.image_url ?? null,
        })),
      );
    } catch (e) {
      console.error('B2BReturnPage: fetchProducts error', e);
    } finally {
      setLoadingProds(false);
    }
  }, [ctx]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // ── Filtered product list ─────────────────────────────────────────────────

  const filtered = useMemo(() => {
    if (!search.trim()) return products;
    const q = search.trim().toLowerCase();
    return products.filter(
      p => p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q),
    );
  }, [products, search]);

  // ── Line management ───────────────────────────────────────────────────────

  const handleAddProduct = useCallback((p: ReturnProduct) => {
    setLines(prev => {
      if (prev.some(l => l.product.id === p.id)) return prev;
      return [...prev, { product: p, qty: 1, return_reason: '' }];
    });
    setSearch('');
  }, []);

  const handleQtyChange = useCallback((productId: string, qty: number) => {
    setLines(prev => prev.map(l =>
      l.product.id === productId ? { ...l, qty } : l,
    ));
  }, []);

  const handleReasonChange = useCallback((productId: string, reason: string) => {
    setLines(prev => prev.map(l =>
      l.product.id === productId ? { ...l, return_reason: reason } : l,
    ));
  }, []);

  const handleRemove = useCallback((productId: string) => {
    setLines(prev => prev.filter(l => l.product.id !== productId));
  }, []);

  // ── Submit RMA ────────────────────────────────────────────────────────────

  const { remindIfNeeded: remindTerms } = useB2BTerms();
  const handleSubmit = useCallback(async () => {
    if (!ctx || lines.length === 0 || submitting) return;
    await remindTerms();
    setSubmitting(true);
    setError(null);

    const p_lines = lines.map(l => ({
      product_id:    l.product.id,
      requested_qty: l.qty,
      unit_price:    l.product.price,
      return_reason: l.return_reason.trim() || null,
    }));

    const { data, error: rpcErr } = await supabase.rpc('submit_rma', {
      p_company_id:    ctx.company_id,
      p_client_id:     ctx.client_id,
      p_agent_id:      ctx.agent_id,
      p_source_order:  null,
      p_return_reason: globalReason.trim() || null,
      p_lines,
    });

    if (rpcErr) {
      setError(rpcErr.message);
      setSubmitting(false);
      return;
    }

    setRmaId(data as string);
    setSuccess(true);
    setLines([]);
    setGlobalReason('');
    setSubmitting(false);
  }, [ctx, lines, globalReason, submitting, remindTerms]);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  if (returnsEnabled === false) return <Navigate to="/b2b/orders" replace />;

  // No session / no active client
  if (!session || !ctx) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center" dir="rtl">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-orange-100 flex items-center justify-center">
            <RotateCcw className="w-8 h-8 text-orange-400" />
          </div>
          <p className="text-sm text-gray-600">יש לבחור לקוח לפני שניתן לבצע בקשת החזרה.</p>
          <button
            onClick={() => navigate('/b2b/agent')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-orange-500
                       text-white text-sm font-semibold hover:bg-orange-600 transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
            בחר לקוח
          </button>
        </div>
      </div>
    );
  }

  // ── Success state ─────────────────────────────────────────────────────────

  if (success) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center" dir="rtl">
        <div className="flex flex-col items-center gap-5">
          <div className="w-20 h-20 rounded-3xl bg-green-100 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
          </div>
          <div className="space-y-1.5">
            <h2 className="text-xl font-bold text-gray-900">בקשת ההחזרה נשלחה!</h2>
            <p className="text-sm text-gray-500 leading-relaxed max-w-xs">
              הבקשה הועברה לצוות שלנו לטיפול. תקבל עדכון לאחר הבדיקה.
            </p>
            {rmaId && (
              <p className="text-[11px] text-gray-400 font-mono mt-1">
                מזהה בקשה: #{rmaId.slice(-8).toUpperCase()}
              </p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full max-w-xs">
            <button
              onClick={() => {
                setSuccess(false);
                setRmaId(null);
              }}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5
                         rounded-xl border border-orange-300 text-orange-600
                         text-sm font-semibold hover:bg-orange-50 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              בקשה נוספת
            </button>
            <button
              onClick={() => navigate('/b2b/orders', { state: navState })}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5
                         rounded-xl bg-primary-600 text-white text-sm font-semibold
                         hover:bg-primary-700 transition-colors"
            >
              <ClipboardList className="w-4 h-4" />
              ההזמנות שלי
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main form ─────────────────────────────────────────────────────────────

  return (
    <div className="max-w-2xl mx-auto px-4 py-5 space-y-4 pb-24" dir="rtl">

      {/* Page header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-orange-100 flex items-center justify-center">
          <RotateCcw className="w-5 h-5 text-orange-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">בקשת החזרה</h1>
          <p className="text-xs text-gray-500">
            בחר מוצרים להחזרה — הבקשה תועבר לאישור
          </p>
        </div>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-2.5 px-4 py-3 bg-amber-50
                      border border-amber-200 rounded-2xl text-xs text-amber-800">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
        <p>
          <span className="font-semibold">לתשומת לבך — </span>
          בקשת ההחזרה ממתינה לאישור הצוות שלנו. המלאי יעודכן רק לאחר האישור.
        </p>
      </div>

      {/* ── Product search ─────────────────────────────────────────────── */}
      <div className="space-y-2">
        <h2 className="text-sm font-semibold text-gray-700">חיפוש מוצר להחזרה</h2>
        <div className="relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="חפש לפי שם מוצר או מק״ט…"
            className="w-full pr-9 pl-4 py-2.5 rounded-xl border border-gray-200
                       focus:outline-none focus:ring-2 focus:ring-orange-300 text-sm
                       placeholder:text-gray-300"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Product list */}
        {search.trim() && (
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            {loadingProds ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-400">
                לא נמצאו מוצרים
              </div>
            ) : (
              <div className="divide-y divide-gray-50 max-h-72 overflow-y-auto py-1">
                {filtered.slice(0, 20).map(p => (
                  <ProductRow
                    key={p.id}
                    product={p}
                    onAdd={handleAddProduct}
                  />
                ))}
                {filtered.length > 20 && (
                  <p className="text-center text-[11px] text-gray-400 py-2">
                    + {filtered.length - 20} תוצאות נוספות — צמצם את החיפוש
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Return basket ──────────────────────────────────────────────── */}
      {lines.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700">
              פריטים להחזרה
            </h2>
            <span className="text-xs text-orange-600 font-semibold bg-orange-100
                             px-2.5 py-0.5 rounded-full">
              {lines.length} פריטים
            </span>
          </div>

          <div className="space-y-2">
            {lines.map(line => (
              <ReturnLineRow
                key={line.product.id}
                line={line}
                onQtyChange={handleQtyChange}
                onReasonChange={handleReasonChange}
                onRemove={handleRemove}
              />
            ))}
          </div>

          {/* Global reason */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-600">
              סיבת ההחזרה הכללית (אופציונלי)
            </label>
            <textarea
              rows={2}
              value={globalReason}
              onChange={e => setGlobalReason(e.target.value)}
              placeholder="תאר את הסיבה הכללית להחזרה…"
              className="w-full text-sm rounded-xl border border-gray-200 px-3 py-2.5
                         focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none
                         placeholder:text-gray-300"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2.5 px-4 py-3 bg-red-50
                            border border-red-200 rounded-xl text-xs text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">שגיאה בשליחת הבקשה</p>
                <p className="mt-0.5">{error}</p>
              </div>
            </div>
          )}

          {/* Submit button */}
          <button
            onClick={handleSubmit}
            disabled={submitting || lines.length === 0}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl
                       bg-orange-500 hover:bg-orange-600 active:bg-orange-700
                       text-white font-bold text-sm transition-colors
                       disabled:opacity-60 active:scale-[.98]"
          >
            {submitting
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Send      className="w-4 h-4" />
            }
            {submitting ? 'שולח בקשה…' : 'שלח בקשת החזרה'}
          </button>
        </div>
      )}

      {/* ── Empty state (no lines yet) ────────────────────────────────── */}
      {lines.length === 0 && !search.trim() && (
        <div className="flex flex-col items-center justify-center py-20 gap-5">
          <div className="w-20 h-20 rounded-3xl bg-orange-100 flex items-center justify-center">
            <RotateCcw className="w-10 h-10 text-orange-300" />
          </div>
          <div className="text-center space-y-1.5">
            <h2 className="text-lg font-bold text-gray-800">אין פריטים להחזרה</h2>
            <p className="text-sm text-gray-500 leading-relaxed max-w-xs">
              חפש מוצר בשדה החיפוש למעלה כדי להוסיפו לבקשת ההחזרה.
            </p>
          </div>
          <button
            onClick={() => navigate('/b2b/catalog', { state: navState })}
            className="flex items-center gap-2 px-5 py-3 rounded-2xl
                       border border-gray-200 text-gray-600 font-semibold text-sm
                       hover:bg-gray-50 transition-colors"
          >
            <BookOpen className="w-4 h-4" />
            עבור לקטלוג
          </button>
        </div>
      )}
    </div>
  );
}
